import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Trash2 } from "lucide-react";
import { browserSupportsWebAuthn, startRegistration } from "@simplewebauthn/browser";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { EmptyRow, SkeletonRows } from "@/components/TableStates";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { del, get, post } from "@/lib/api";
import { date } from "@/lib/format";
import { passkeyErrorMessage } from "@/lib/passkeys";

interface Passkey { id: string; name: string; createdAt: string; lastUsedAt: string | null }

export function Passkeys() {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const supported = browserSupportsWebAuthn();
  const passkeys = useQuery({ queryKey: ["passkeys"], queryFn: () => get<Passkey[]>("/api/passkeys") });

  const add = useMutation({
    mutationFn: async () => {
      const optionsJSON = await post<Parameters<typeof startRegistration>[0]["optionsJSON"]>("/api/passkeys/options");
      const response = await startRegistration({ optionsJSON });
      return post<Passkey>("/api/passkeys", { response, name: name.trim() || undefined });
    },
    onSuccess: () => { setName(""); qc.invalidateQueries({ queryKey: ["passkeys"] }); toast.success("Passkey hinzugefügt"); },
    onError: (e) => toast.error(passkeyErrorMessage(e, "Passkey konnte nicht angelegt werden")),
  });
  const remove = useMutation({
    mutationFn: (id: string) => del(`/api/passkeys/${encodeURIComponent(id)}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["passkeys"] }); toast.success("Passkey gelöscht"); },
    onError: (e) => toast.error(e.message),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    add.mutate();
  }

  return (
    <div className="space-y-5">
      <PageHeader title="Passkeys" />
      <p className="max-w-prose text-sm text-muted-foreground">
        Mit einem Passkey meldest du dich per Touch ID, Face ID oder Sicherheitsschlüssel an. Das Passwort funktioniert weiterhin.
      </p>
      {supported ? (
        <form onSubmit={submit} className="flex max-w-md flex-wrap items-end gap-2">
          <div className="min-w-48 flex-1 space-y-2">
            <Label htmlFor="pk-name">Name</Label>
            <Input id="pk-name" placeholder="z. B. MacBook" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <Button type="submit" disabled={add.isPending}><KeyRound /> Passkey hinzufügen</Button>
        </form>
      ) : (
        <p className="text-sm text-muted-foreground">Dieser Browser unterstützt keine Passkeys.</p>
      )}
      <Table className="table-fixed">
        <colgroup><col /><col style={{ width: "7rem" }} /><col className="max-sm:hidden" style={{ width: "8rem" }} /><col style={{ width: "3rem" }} /></colgroup>
        <TableHeader>
          <TableRow className="hover:bg-transparent [&>th]:text-muted-foreground">
            <TableHead>Name</TableHead><TableHead>Angelegt</TableHead><TableHead className="max-sm:hidden">Zuletzt benutzt</TableHead>
            <TableHead><span className="sr-only">Löschen</span></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {passkeys.isPending ? <SkeletonRows columns={4} rows={2} /> : (passkeys.data?.length ?? 0) === 0 ? (
            <EmptyRow columns={4}>Noch keine Passkeys.</EmptyRow>
          ) : passkeys.data?.map((p) => (
            <TableRow key={p.id} className="h-12">
              <TableCell className="overflow-hidden font-medium"><span className="block truncate" title={p.name}>{p.name}</span></TableCell>
              <TableCell className="tabular-nums text-muted-foreground">{date(p.createdAt)}</TableCell>
              <TableCell className="tabular-nums text-muted-foreground max-sm:hidden">{p.lastUsedAt ? date(p.lastUsedAt) : "—"}</TableCell>
              <TableCell className="text-right">
                <Button variant="ghost" size="icon-sm" aria-label={`Passkey ${p.name} löschen`} title="Passkey löschen"
                  className="text-muted-foreground hover:text-destructive" disabled={remove.isPending}
                  onClick={() => confirm(`Passkey „${p.name}“ löschen? Das Gerät kann sich danach nur noch mit Passwort anmelden.`) && remove.mutate(p.id)}>
                  <Trash2 />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
